import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import Box from '@amzn/meridian/box'
import geopinTokens from '@amzn/meridian-tokens/base/icon/geopin'
import { useBundle } from '@amzn/react-arb-tools'
import { getSelectedLocale } from '@atredux/selectors/localeSelector'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { IActionCenterNotification } from '../types'
import { ACKNOWLEDGE, ACKNOWLEDGE_ERROR } from '../constants'
import { resolvePrimaryAction } from '../resolveAction'
import { BackHeader } from './Headers'

interface INotificationDetailViewProps {
    notification: IActionCenterNotification
    currentStoreId?: string
    /**
     * False when opened from the view-only "All stores" tab: the producer's action is not rendered.
     * Acknowledge is deliberately NOT gated on this. "View only" means the associate cannot work the
     * task from here (Solve needs the store's tools), whereas Acknowledge only marks the caller's
     * own inbox copy READ, which needs no store context. Gating it would leave the cross-store
     * bell count unclearable without switching stores.
     */
    showActions?: boolean
    onBack: () => void
    /** Resolves once the service has accepted the READ transition; rejects otherwise. */
    onAcknowledge: (notification: IActionCenterNotification) => Promise<void>
}

const formatAbsoluteTime = (date: Date, locale: string): string =>
    date.toLocaleString(locale, { hour: 'numeric', minute: '2-digit', year: 'numeric', month: 'numeric', day: 'numeric' })

/**
 * Full-card view: location, title, absolute time, then whatever the producer rendered into the
 * card (summary, details, plain-text body). Acknowledge marks an unread notification READ, which
 * removes it from the unread feed; a notification that is already READ has nothing to acknowledge.
 * Acknowledge is offered on both tabs; only the producer's action is limited to the Current store tab.
 */
export const NotificationDetailView: React.FC<INotificationDetailViewProps> = ({
    notification,
    currentStoreId,
    showActions = true,
    onBack,
    onAcknowledge,
}) => {
    const locale = useSelector(getSelectedLocale)
    const [bundle] = useBundle(BUNDLE_NAME)
    const [isAcknowledging, setAcknowledging] = useState(false)
    const [acknowledgeFailed, setAcknowledgeFailed] = useState(false)
    const action = showActions ? resolvePrimaryAction(notification, currentStoreId) : undefined

    const acknowledge = async () => {
        setAcknowledging(true)
        setAcknowledgeFailed(false)
        try {
            await onAcknowledge(notification)
        } catch (error) {
            console.error('Failed to acknowledge notification', notification.notificationId, error)
            setAcknowledgeFailed(true)
            setAcknowledging(false)
        }
    }

    // Avoid showing the same text twice when a producer put its summary in both slots.
    const showBody = !!notification.body && notification.body !== notification.summary && notification.body !== notification.details

    return (
        <div className="action-center-detail" data-testid="notification-detail">
            <BackHeader onBack={onBack} />
            <Column spacing="300" spacingInset="300 400">
                {notification.location && (
                    <Row spacing="100" alignmentVertical="center">
                        <Icon tokens={geopinTokens} />
                        <Text type="b200" color="secondary" data-testid="detail-location">{notification.location}</Text>
                    </Row>
                )}
                <Text type="h300" data-testid="detail-title">{notification.title}</Text>
                <Text type="b200" color="secondary" data-testid="detail-time">
                    {formatAbsoluteTime(notification.createdAt, locale)}
                </Text>
                {notification.summary && <Text type="b300" data-testid="detail-summary">{notification.summary}</Text>}
                {notification.details && (
                    <Text type="b300" color="secondary" data-testid="detail-details">{notification.details}</Text>
                )}
                {showBody && <Text type="b300" data-testid="detail-body">{notification.body}</Text>}
            </Column>
            <Column spacing="200" spacingInset="300 400">
                {acknowledgeFailed && (
                    <Box type="outline" spacingInset="200 300">
                        <Text type="b200" color="error" data-testid="acknowledge-error">{t(bundle, ACKNOWLEDGE_ERROR)}</Text>
                    </Box>
                )}
                {notification.status !== 'READ' && (
                    <Button
                        type="primary"
                        size="large"
                        onClick={acknowledge}
                        disabled={isAcknowledging}
                        data-testid="acknowledge-button"
                    >
                        {t(bundle, ACKNOWLEDGE)}
                    </Button>
                )}
                {action && (
                    <Button type="secondary" size="large" disabled={!action.enabled} onClick={action.navigate} data-testid="detail-action">
                        {action.label}
                    </Button>
                )}
            </Column>
        </div>
    )
}
